import { useEffect, useState } from 'react';
import {
  changePassword,
  clearToken,
  fetchContent,
  forgotPassword,
  getToken,
  login,
  resetPassword,
  saveContent,
  setToken,
} from './api';
import { labelFor, SECTION_LABELS } from './labels';

/* ------------------------------------------------------------------ */
/* Immutable helpers                                                   */
/* ------------------------------------------------------------------ */
type Path = (string | number)[];

function setByPath(obj: any, path: Path, value: any): any {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(obj)) {
    const copy = obj.slice();
    copy[head as number] = setByPath(obj[head as number], rest, value);
    return copy;
  }
  return { ...obj, [head]: setByPath(obj?.[head], rest, value) };
}

/* ------------------------------------------------------------------ */
/* Recursive field renderer                                            */
/* ------------------------------------------------------------------ */
function Field({
  label,
  value,
  path,
  onChange,
  depth,
}: {
  label: string;
  value: any;
  path: Path;
  onChange: (path: Path, value: any) => void;
  depth: number;
}) {
  if (typeof value === 'string') {
    const multiline = value.length > 55 || value.includes('\n') || value.includes('<');
    return (
      <label className="block mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(path, e.target.value)}
            rows={Math.min(8, Math.max(2, Math.ceil(value.length / 60)))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(path, e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
          />
        )}
      </label>
    );
  }

  if (Array.isArray(value)) {
    // Array of plain strings → render each as a simple input.
    const allStrings = value.every((v) => typeof v === 'string');
    return (
      <fieldset className="mb-5 border border-blush-100 rounded-xl p-4 bg-white">
        <legend className="px-2 text-sm font-semibold text-champagne-600">{label}</legend>
        {value.map((item, i) =>
          allStrings ? (
            <Field
              key={i}
              label={`${i + 1}.`}
              value={item}
              path={[...path, i]}
              onChange={onChange}
              depth={depth + 1}
            />
          ) : (
            <div key={i} className="mb-4 border-l-2 border-champagne-200 pl-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                Eintrag {i + 1}
              </p>
              <Field
                label={`Eintrag ${i + 1}`}
                value={item}
                path={[...path, i]}
                onChange={onChange}
                depth={depth + 1}
              />
            </div>
          )
        )}
      </fieldset>
    );
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value);
    return (
      <div className={depth === 0 ? '' : 'mb-2'}>
        {keys.map((k) => (
          <Field
            key={k}
            label={labelFor(k)}
            value={value[k]}
            path={[...path, k]}
            onChange={onChange}
            depth={depth + 1}
          />
        ))}
      </div>
    );
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* Login screen                                                        */
/* ------------------------------------------------------------------ */
function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setForgotMsg('');
    setBusy(true);
    try {
      const token = await login(email.trim(), password);
      setToken(token);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    setError('');
    setForgotMsg('');
    setForgotBusy(true);
    try {
      const sentTo = await forgotPassword();
      setForgotMsg(
        `Wir haben eine E-Mail mit einem Link zum Zurücksetzen an deine hinterlegte Adresse${
          sentTo ? ` (${sentTo})` : ''
        } gesendet. Bitte schau in dein Postfach (ggf. auch im Spam-Ordner).`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'E-Mail konnte nicht gesendet werden.');
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-blush px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-blush-100 p-8">
        <div className="text-center mb-6">
          <p className="font-script text-3xl text-champagne-500">Gesichtszauber</p>
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mt-1">Bearbeitungsraum</p>
        </div>
        <form onSubmit={submit}>
          <label className="block mb-3">
            <span className="block text-sm font-medium text-gray-700 mb-1">E-Mail</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-gray-700 mb-1">Passwort</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          {forgotMsg && <p className="text-sm text-green-600 mb-3">{forgotMsg}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-champagne-500 text-white rounded-full py-2.5 text-sm font-medium hover:bg-champagne-600 transition-colors disabled:opacity-60"
          >
            {busy ? 'Anmelden …' : 'Anmelden'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={forgot}
            disabled={forgotBusy}
            className="text-sm text-gray-500 hover:text-champagne-600 underline underline-offset-2 disabled:opacity-60"
          >
            {forgotBusy ? 'E-Mail wird gesendet …' : 'Passwort vergessen?'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Reset screen (via e-mail link ?reset=TOKEN)                         */
/* ------------------------------------------------------------------ */
function ResetScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (next.length < 4) {
      setError('Das neue Passwort muss mindestens 4 Zeichen haben.');
      return;
    }
    if (next !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, next);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zurücksetzen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-blush px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-blush-100 p-8">
        <div className="text-center mb-6">
          <p className="font-script text-3xl text-champagne-500">Gesichtszauber</p>
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mt-1">Neues Passwort</p>
        </div>
        {done ? (
          <div className="text-center">
            <p className="text-sm text-green-600 mb-5">
              Dein Passwort wurde geändert. Du kannst dich jetzt mit dem neuen Passwort anmelden.
            </p>
            <button
              onClick={onDone}
              className="w-full bg-champagne-500 text-white rounded-full py-2.5 text-sm font-medium hover:bg-champagne-600"
            >
              Zur Anmeldung
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label className="block mb-3">
              <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort</span>
              <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                autoFocus
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
              />
            </label>
            <label className="block mb-4">
              <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort bestätigen</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
              />
            </label>
            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-champagne-500 text-white rounded-full py-2.5 text-sm font-medium hover:bg-champagne-600 disabled:opacity-60"
            >
              {busy ? 'Speichern …' : 'Passwort speichern'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Change-password panel                                               */
/* ------------------------------------------------------------------ */
function PasswordPanel({ onClose, onExpired }: { onClose: () => void; onExpired: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMsg('');
    if (next.length < 4) {
      setError('Das neue Passwort muss mindestens 4 Zeichen haben.');
      return;
    }
    if (next !== confirm) {
      setError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setMsg('Passwort erfolgreich geändert.');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      const m = err instanceof Error ? err.message : 'Fehler.';
      setError(m);
      if (m.includes('abgelaufen')) onExpired();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-gray-800">Passwort ändern</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Schließen">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <form onSubmit={submit}>
          <label className="block mb-3">
            <span className="block text-sm font-medium text-gray-700 mb-1">Aktuelles Passwort</span>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          <label className="block mb-3">
            <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort</span>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-gray-700 mb-1">Neues Passwort bestätigen</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-champagne-500 focus:ring-1 focus:ring-champagne-500 outline-none"
            />
          </label>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          {msg && <p className="text-sm text-green-600 mb-3">{msg}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-600 rounded-full py-2 text-sm hover:bg-gray-50"
            >
              Schließen
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 bg-champagne-500 text-white rounded-full py-2 text-sm font-medium hover:bg-champagne-600 disabled:opacity-60"
            >
              {busy ? 'Speichern …' : 'Ändern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editor (authenticated)                                              */
/* ------------------------------------------------------------------ */
function EditorScreen({ onLogout }: { onLogout: () => void }) {
  const [content, setContent] = useState<any | null>(null);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchContent()
      .then(setContent)
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Fehler beim Laden.'));
  }, []);

  const update = (path: Path, value: any) => {
    setContent((prev: any) => setByPath(prev, path, value));
  };

  const save = async () => {
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');
    try {
      await saveContent(content);
      setSaveMsg('Änderungen gespeichert. Die Webseite ist aktualisiert.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Fehler.';
      setSaveErr(m);
      if (m.includes('abgelaufen')) onLogout();
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return <div className="p-8 text-center text-red-600">{loadError}</div>;
  }
  if (!content) {
    return <div className="p-8 text-center text-gray-500">Inhalte werden geladen …</div>;
  }

  const sectionKeys = Object.keys(content);

  return (
    <div className="min-h-screen bg-warm-light pb-32">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-blush-100 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <p className="font-script text-2xl text-champagne-500 leading-none">Gesichtszauber</p>
            <p className="text-[11px] tracking-[0.2em] uppercase text-gray-400">Bearbeitungsraum</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-gray-500 hover:text-champagne-500 px-3 py-2"
              title="Webseite in neuem Tab öffnen"
            >
              Webseite ansehen
            </a>
            <button
              onClick={() => setShowPassword(true)}
              className="text-sm text-gray-500 hover:text-champagne-500 px-3 py-2"
            >
              Passwort ändern
            </button>
            <button
              onClick={onLogout}
              className="text-sm text-gray-500 hover:text-red-500 px-3 py-2"
            >
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-6">
        <p className="text-sm text-gray-500 mb-6">
          Hier können Sie alle Texte Ihrer Webseite bearbeiten. Ändern Sie die gewünschten Felder und
          klicken Sie anschließend auf <strong>„Speichern“</strong>. Tipp: Mit
          <code className="mx-1 px-1 bg-blush-50 rounded">&lt;strong&gt;Wort&lt;/strong&gt;</code>
          machen Sie einzelne Wörter fett.
        </p>

        {(saveMsg || saveErr) && (
          <div
            className={`mb-6 rounded-lg px-4 py-3 text-sm ${
              saveErr ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
            }`}
          >
            {saveErr || saveMsg}
          </div>
        )}

        {sectionKeys.map((key) => (
          <section
            key={key}
            className="mb-6 bg-blush-50/40 rounded-2xl border border-blush-100 p-5"
          >
            <h2 className="font-display text-xl text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-6 h-[2px] bg-champagne-400"></span>
              {SECTION_LABELS[key] ?? labelFor(key)}
            </h2>
            <Field
              label={SECTION_LABELS[key] ?? labelFor(key)}
              value={content[key]}
              path={[key]}
              onChange={update}
              depth={0}
            />
          </section>
        ))}
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-blush-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-400 hidden sm:block">
            Änderungen werden erst nach dem Speichern öffentlich sichtbar.
          </span>
          <button
            onClick={save}
            disabled={saving}
            className="ml-auto bg-champagne-500 text-white rounded-full px-8 py-2.5 text-sm font-medium hover:bg-champagne-600 transition-colors disabled:opacity-60 shadow-md"
          >
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </div>

      {showPassword && (
        <PasswordPanel onClose={() => setShowPassword(false)} onExpired={onLogout} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function Editor() {
  const [authed, setAuthed] = useState<boolean>(() => !!getToken());
  const [resetToken, setResetToken] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset');
  });

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  // A reset link takes priority over everything else.
  if (resetToken) {
    return (
      <ResetScreen
        token={resetToken}
        onDone={() => {
          // Drop ?reset from the URL and go to the login screen.
          window.history.replaceState({}, '', '/bearbeiten');
          clearToken();
          setAuthed(false);
          setResetToken(null);
        }}
      />
    );
  }

  if (!authed) {
    return <LoginScreen onSuccess={() => setAuthed(true)} />;
  }
  return <EditorScreen onLogout={logout} />;
}
